export class BenefitDetailPrettifier {
  static prettify(benefitDescription?: string | null) {
    const { flag } = FeatureFlags.use()

    const enabled = flag<boolean>('format-benefit-descriptions', false).value

    if (!enabled || _isNil(benefitDescription)) return benefitDescription

    // 1) Normalize <br> variants into newlines
    const normalized = benefitDescription.replace(/<br\s*\/?>/gi, '\n')

    // Early single-line check (after trimming empty lines later)
    const rawLines = normalized
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)

    const isHeaderLine = (s: string): boolean => /:\s*$/i.test(s)
    const isNetwork = (s: string): boolean => /^(in-network|out-of-network)$/i.test(s)
    const capNetwork = (s: string): string =>
      /^in-network$/i.test(s) ? 'In-Network' : /^out-of-network$/i.test(s) ? 'Out-of-Network' : s

    const escapeHtml = (s: string): string =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const lines: string[] = rawLines

    // If there's literally one non-empty line and no headers anywhere
    const hasAnyHeader = lines.some(isHeaderLine)
    if (lines.length === 1 && !hasAnyHeader) {
      return `${escapeHtml(lines[0])}`
    }

    let html: string[] = []
    let buffer: string[] = []

    // Context
    let currentNetworkContext: string | null = null // persists across headers
    let pendingNetwork: string | null = null // attaches to the very next header
    let emittedAnyHeader = false // track if we've emitted any header yet
    let currentlyUnderHeader = false // are we inside a header section?

    const flushBufferUnderHeader = (): void => {
      if (buffer.length === 0) return
      // Under a header -> always ul, even for a single item
      html.push('<ul>')
      for (const line of buffer) {
        html.push(`<li>${escapeHtml(line)}</li>`)
      }
      html.push('</ul>')
      buffer = []
    }

    const emitHeader = (title: string, networkOverride: string | null): void => {
      // Finish previous section content
      if (currentlyUnderHeader) flushBufferUnderHeader()

      // Determine network annotation + update persistent context
      if (networkOverride) currentNetworkContext = networkOverride
      const networkToShow = networkOverride ?? currentNetworkContext
      const text = networkToShow ? `${title} (${networkToShow}):` : `${title}:`

      html.push(`<h4>${escapeHtml(text)}</h4>`)
      emittedAnyHeader = true
      currentlyUnderHeader = true // content that follows belongs to this header
    }

    const findNextNonEmptyIndex = (start: number): number => {
      for (let j = start + 1; j < lines.length; j++) {
        if (lines[j].length > 0) return j
      }
      return -1
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]

      // Network markers
      if (isNetwork(line)) {
        const network = capNetwork(line)
        const nextIdx = findNextNonEmptyIndex(i)

        // If the next non-empty line is a header, attach and persist context
        if (nextIdx !== -1 && isHeaderLine(lines[nextIdx])) {
          pendingNetwork = network
          currentNetworkContext = network // becomes default for following headers
          continue
        }

        // If no header directly before or after, this network line becomes its own header
        emitHeader(network, null) // "<h4>In-Network:</h4>"
        currentNetworkContext = network // persist for future headers
        continue
      }

      // Headers
      if (isHeaderLine(line)) {
        const title = line.replace(/:\s*$/i, '')
        let attachedNetwork = pendingNetwork
        pendingNetwork = null

        const nextIdx = findNextNonEmptyIndex(i)
        if (!attachedNetwork && nextIdx !== -1 && isNetwork(lines[nextIdx])) {
          attachedNetwork = capNetwork(lines[nextIdx])
          currentNetworkContext = attachedNetwork // persist
          i = nextIdx // consume the network line
        }

        emitHeader(title, attachedNetwork)
        continue
      }

      // Regular content line
      buffer.push(line)
    }

    // Final flush
    if (currentlyUnderHeader) {
      flushBufferUnderHeader() // always UL under a header (even 1 item)
    } else {
      // No headers at all in the entire input:
      // wrap the whole thing as a single <p>, preserving line breaks
      if (!hasAnyHeader) {
        const joined = buffer.map(escapeHtml).join('<br>')
        return `<p>${joined}</p>`
      }
    }

    return html.join('\n')
  }
}
